/*
 * ajluxe-checkout-telemetry.js
 *
 * Counts checkout-button clicks that never lead to a navigation.
 *
 * Why this exists: on Sep 13 a Clarity session (vzuxxp/erx79v) tapped SECURE CHECKOUT
 * five times over ~16s with $71.99 in the bag and the page never moved. Clarity's own
 * dead-click and rage-click detectors did NOT flag it: the taps were seconds apart on a
 * genuinely interactive <button>, so neither threshold fires. And exit-URL is useless
 * here because Clarity does not run on Shopify's checkout domain, so a shopper who
 * checks out fine records the same exit page as one who is stuck.
 *
 * So we measure it directly: on every checkout click, arm a timer. A real checkout is a
 * full-page POST to /cart, which fires pagehide when the new document commits. If
 * pagehide never arrives, the click did nothing.
 *
 * Two thresholds so slow networks do not read as failures:
 *   SLOW_MS  (5s)  - still here, probably just slow. Logged, not counted as dead.
 *   DEAD_MS  (12s) - still here. Nothing plausibly takes this long. Counted as dead.
 *
 * Known undercount: a backgrounded mobile tab throttles timers, so some real dead
 * clicks go unrecorded. That errs toward missing failures, never toward inventing them.
 *
 * Remove this file (and its <script> tag in layout/theme.liquid) once the question is
 * settled. It is a measurement, not a feature.
 */
(function () {
  "use strict";

  var SLOW_MS = 5000;
  var DEAD_MS = 12000;

  /* Every control that is supposed to take a shopper to Shopify checkout. A theme-wide
     grep found exactly four, and they fail in different ways, so each is tagged
     separately by sourceOf() below:
       .ajluxe-cart-checkout / .cart-modal__btn--checkout
                              - the drawer and /cart submit buttons (site-template.liquid)
       .aj-cart-sticky__btn   - sticky bag bar on product and collection pages, an <a>
       .aj-cart-bar__checkout - blog cart recovery bar (article-template.liquid), an <a>.
                                Easy to miss: it is a different component from the sticky
                                bar above, and blog is our biggest traffic source.
     The last two generic entries catch anything added later without a code change here:
     any submit named "checkout", and any link pointing at /checkout. */
  var SELECTOR = [
    ".ajluxe-cart-checkout",
    ".cart-modal__btn--checkout",
    ".aj-cart-sticky__btn",
    ".aj-cart-bar__checkout",
    '[name="checkout"]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]'
  ].join(",");

  var pending = null;   // { slow: timeoutId, dead: timeoutId }
  var deadCount = 0;    // dead clicks so far this session
  var navigating = false;

  function clarity() {
    /* The Clarity tag installs window.clarity as a queueing stub immediately, so early
       calls are safe. Guard anyway: it is a third-party script and can fail to load. */
    if (typeof window.clarity !== "function") return;
    try {
      window.clarity.apply(null, arguments);
    } catch (e) {}
  }

  function mirror(name, params) {
    /* Mirror into GA4 / dataLayer so the count is checkable in two places, not one. */
    try {
      var payload = Object.assign({
        page_path: window.location.pathname
      }, params || {});
      if (typeof window.gtag === "function") window.gtag("event", name, payload);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload));
    } catch (e) {}
  }

  function sourceOf(el) {
    if (el.closest(".aj-cart-bar")) return "blog_cart_bar";
    if (el.closest(".aj-cart-sticky")) return "sticky_bar";
    if (el.closest(".js-drawer, .drawer")) return "drawer";
    if (window.location.pathname.indexOf("/cart") === 0) return "cart_page";
    return "other";
  }

  function cartState() {
    /* Diagnostic only. Read what the shopper can actually SEE at the moment they click,
       never the header badge: `.header-cart__count` is stale until the drawer re-renders
       (measured: server cart 2, badge still showing 1), and a wrong number here would
       quietly poison the analysis. Every source below is re-rendered from the live cart
       at the moment its component is shown. Missing is better than wrong, so each falls
       back to an empty string. */
    var state = { items: "", total: "" };
    try {
      /* Drawer: "Your Bag (2)" in the header, and the rendered subtotal. */
      var drawerTitle = document.querySelector(".js-drawer .drawer__title, .drawer .drawer__title");
      if (drawerTitle) {
        var m = (drawerTitle.textContent || "").match(/\((\d+)\)/);
        if (m) state.items = m[1];
      }
      /* Blog cart recovery bar keeps its own freshly-written count and total. */
      if (!state.items) {
        var barCount = document.querySelector("[data-aj-cart-bar-count]");
        if (barCount) state.items = (barCount.textContent || "").replace(/\D+/g, "").slice(0, 8);
      }
      var cost = document.querySelector("[data-cart-cost]")
             || document.querySelector("[data-aj-cart-bar-total]");
      if (cost) state.total = (cost.textContent || "").trim().slice(0, 16);
    } catch (e) {}
    return state;
  }

  function clearPending() {
    if (!pending) return;
    clearTimeout(pending.slow);
    clearTimeout(pending.dead);
    pending = null;
  }

  document.addEventListener("click", function (event) {
    var el = event.target && event.target.closest && event.target.closest(SELECTOR);
    if (!el) return;

    var source = sourceOf(el);
    var state = cartState();

    clarity("set", "aj_checkout_source", source);
    if (state.items) clarity("set", "aj_checkout_items", state.items);
    clarity("event", "aj_checkout_click");
    mirror("aj_checkout_click", { checkout_source: source, cart_items: state.items, cart_total: state.total });

    /* A second click while one is already pending is itself the signal we are chasing:
       the shopper is retrying because nothing happened. Keep the original timers running
       so the dead verdict is measured from the FIRST click, and log the retry. */
    if (pending) {
      clarity("event", "aj_checkout_retry_click");
      mirror("aj_checkout_retry_click", { checkout_source: source });
      return;
    }

    pending = {
      slow: setTimeout(function () {
        if (navigating) return;
        clarity("event", "aj_checkout_slow");
        mirror("aj_checkout_slow", { checkout_source: source, waited_ms: SLOW_MS });
      }, SLOW_MS),

      dead: setTimeout(function () {
        if (navigating) return;
        deadCount += 1;
        clarity("set", "aj_checkout_outcome", "stuck");
        clarity("set", "aj_checkout_dead_count", String(deadCount));
        clarity("event", "aj_checkout_dead");
        /* Force Clarity to keep this session: a stuck checkout is exactly the recording
           we want to watch, and sampling might otherwise throw it away. */
        clarity("upgrade", "checkout_dead");
        mirror("aj_checkout_dead", {
          checkout_source: source,
          waited_ms: DEAD_MS,
          cart_items: state.items,
          cart_total: state.total,
          /* A hidden document means the shopper backgrounded the tab, so the timer may
             have been throttled and the wait is not really DEAD_MS. Tag it so these can
             be excluded from a strict count. */
          doc_hidden: document.visibilityState === "hidden"
        });
        pending = null;
      }, DEAD_MS)
    };
  }, true); /* capture: we see the click even if something downstream stops propagation */

  /* pagehide is the honest "we actually left" signal. It fires on a real navigation and
     on bfcache entry; backgrounding an app does not fire it, which is what we want. */
  window.addEventListener("pagehide", function () {
    navigating = true;
    clearPending();
  });

  /* A click that opens Shopify checkout in the same tab always unloads, but Shop Pay and
     the express buttons can hand off without one. Treat a real beforeunload the same. */
  window.addEventListener("beforeunload", function () {
    navigating = true;
    clearPending();
  });
})();
